import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileSpreadsheet, Loader2, AlertTriangle, Pencil, ListChecks, Globe2, Search,
  RefreshCw, Download, Trash2, Eye, X, Columns3, ReceiptText,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { PdfViewer } from "./PdfViewer";

const HOLDED_COLS = [
  "Num factura", "Fecha dd/mm/yyyy", "Fecha de vencimiento dd/mm/yyyy", "Fecha deducción dd/mm/yyyy",
  "Descripción", "Nombre del contacto", "NIF", "Dirección", "Población", "Código postal", "Provincia", "País",
  "Concepto", "Descripción del producto", "SKU", "Precio unidad", "Unidades", "Descuento %", "IVA %", "Retención %",
  "Inv. Suj. Pasivo (1/0)", "Operación", "Cantidad cobrada", "Fecha de cobro", "Cuenta de pago",
  "Tags separados por -", "Nombre cuenta de gasto", "Num. Cuenta de gasto", "Moneda", "Cambio de moneda", "Almacén",
];
const VISIBLE_COLS = [
  "Num factura", "Fecha dd/mm/yyyy", "Nombre del contacto", "NIF", "País", "Concepto",
  "Precio unidad", "IVA %", "Retención %", "Inv. Suj. Pasivo (1/0)", "Num. Cuenta de gasto", "Moneda",
];

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2">
    <div className={`flex h-7 w-7 items-center justify-center rounded-md ${tone}`}><Icon className="h-3.5 w-3.5" /></div>
    <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p><p className="text-sm font-bold tabular-nums leading-none">{value}</p></div>
  </div>
);

export function CierreLineas({ cierre, onClose }) {
  const qc = useQueryClient();
  const cid = cierre.id;
  const [paisFilter, setPaisFilter] = useState("Todos");
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [q, setQ] = useState("");
  const [allCols, setAllCols] = useState(false);
  const [editing, setEditing] = useState(null); // {id, col}
  const [editValue, setEditValue] = useState("");
  const [pdfLine, setPdfLine] = useState(null);
  const [downloadPais, setDownloadPais] = useState("Todos");

  const linesQ = useQuery({ queryKey: ["cierre-lineas", cid], queryFn: async () => (await api.get(`/contabilidad/cierres/${cid}/lineas`)).data });
  const cuentasQ = useQuery({ queryKey: ["conta-cuentas"], queryFn: async () => (await api.get("/contabilidad/cuentas")).data });

  const data = linesQ.data;
  const lines = data?.lines || [];
  const cuentas = cuentasQ.data?.cuentas || [];
  const cols = allCols ? HOLDED_COLS : VISIBLE_COLS;

  const filtered = useMemo(() => lines.filter((ln) => {
    if (paisFilter === "Retención") { if (ln.grupo !== "retencion") return false; }
    else if (paisFilter !== "Todos" && (ln.grupo === "retencion" || ln.pais !== paisFilter)) return false;
    if (onlyIssues && (!ln.issues || ln.issues.length === 0)) return false;
    if (q) { const hay = Object.values(ln.data || {}).join(" ").toLowerCase(); if (!hay.includes(q.toLowerCase())) return false; }
    return true;
  }), [lines, paisFilter, onlyIssues, q]);

  const editMut = useMutation({
    mutationFn: async ({ id, field, value }) => (await api.patch(`/contabilidad/cierres/${cid}/lineas/${id}`, { field, value })).data,
    onSuccess: (res) => {
      qc.setQueryData(["cierre-lineas", cid], (old) => old ? { ...old, lines: old.lines.map((l) => (l.id === res.line.id ? res.line : l)) } : old);
      qc.invalidateQueries({ queryKey: ["cierre-lineas", cid] });
    },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo guardar la línea")),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/contabilidad/cierres/${cid}/lineas/${id}`)).data,
    onSuccess: () => { toast.success("Línea eliminada del Excel"); qc.invalidateQueries({ queryKey: ["cierre-lineas", cid] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const reexportMut = useMutation({
    mutationFn: async () => (await api.post(`/contabilidad/cierres/${cid}/reexportar`)).data,
    onSuccess: () => { toast.success("ZIP del cierre re-exportado con las correcciones"); qc.invalidateQueries({ queryKey: ["conta-cierres"] }); },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo re-exportar")),
  });

  const startEdit = (id, col, val) => { setEditing({ id, col }); setEditValue(val ?? ""); };
  const cancelEdit = () => { setEditing(null); setEditValue(""); };
  const commitEdit = () => {
    if (!editing) return;
    const ln = lines.find((l) => l.id === editing.id);
    const current = ln?.data?.[editing.col] ?? "";
    if (String(current) !== String(editValue)) editMut.mutate({ id: editing.id, field: editing.col, value: editValue });
    cancelEdit();
  };

  const downloadExcel = async () => {
    try {
      const res = await api.get(`/contabilidad/cierres/${cid}/excel`, { params: { pais: downloadPais === "Retención" ? "retencion" : downloadPais }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Holded_${downloadPais}_${data?.week_label || cid}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const c = data?.counts || {};

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[96vw] w-[96vw] p-0 gap-0 lg:max-w-[92vw]" data-testid="cierre-lineas-dialog">
        <DialogHeader className="border-b px-5 py-3">
          <DialogTitle className="flex flex-wrap items-center gap-2 text-base">
            <FileSpreadsheet className="h-4 w-4 text-primary" /> Líneas del Excel · Cierre {data?.week_label || cierre.week_label}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Edita las líneas que componen el Excel Holded y re-exporta. Las facturas originales ya están bloqueadas y <strong>no se modifican</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[82vh] overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <Stat icon={ListChecks} label="Líneas" value={c.lineas ?? 0} tone="bg-slate-100 text-slate-700" />
            <Stat icon={FileSpreadsheet} label="Holded" value={c.holded ?? 0} tone="bg-emerald-100 text-emerald-700" />
            <Stat icon={ReceiptText} label="Retención" value={c.retencion ?? 0} tone="bg-amber-100 text-amber-700" />
            <Stat icon={AlertTriangle} label="Incidencias" value={c.incidencias ?? 0} tone="bg-rose-100 text-rose-700" />
            <Stat icon={Pencil} label="Editadas" value={c.editadas ?? 0} tone="bg-sky-100 text-sky-700" />
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-lg border bg-white px-2.5 py-1.5">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="w-44 bg-transparent text-sm outline-none" data-testid="cl-search" />
            </div>
            <div className="flex flex-wrap gap-1" data-testid="cl-pais-tabs">
              {["Todos", ...(data?.paises || []), ...((c.retencion ?? 0) > 0 ? ["Retención"] : [])].map((p) => (
                <button key={p} onClick={() => setPaisFilter(p)} data-testid={`cl-pais-${p}`}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${paisFilter === p ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>{p}</button>
              ))}
            </div>
            <label className="ml-auto flex cursor-pointer items-center gap-1.5 text-xs">
              <Checkbox checked={onlyIssues} onCheckedChange={(v) => setOnlyIssues(!!v)} data-testid="cl-only-issues" />Solo con incidencias
            </label>
            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setAllCols((v) => !v)} data-testid="cl-toggle-cols">
              <Columns3 className="mr-1 h-3.5 w-3.5" />{allCols ? "Vista compacta" : "Ver todas las columnas"}
            </Button>
            <Button variant="outline" size="sm" className="h-8" onClick={() => linesQ.refetch()} data-testid="cl-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>

          {linesQ.isLoading ? <Skeleton className="mt-3 h-64 w-full" /> : filtered.length === 0 ? (
            <div className="mt-6 rounded-lg border py-12 text-center text-sm text-muted-foreground" data-testid="cl-empty">No hay líneas para este filtro.</div>
          ) : (
            <div className="mt-3 max-h-[52vh] overflow-auto rounded-xl border">
              <table className="w-full border-collapse text-left">
                <thead className="sticky top-0 z-10 bg-zinc-50/95 backdrop-blur-sm">
                  <tr className="border-b text-[10px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 w-[92px]">Estado</th>
                    {cols.map((col) => <th key={col} className="whitespace-nowrap px-3 py-2">{col}</th>)}
                    <th className="px-3 py-2 w-[80px]" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((ln) => {
                    const hasIssues = ln.issues && ln.issues.length > 0;
                    return (
                      <tr key={ln.id} className={`border-b transition-colors ${hasIssues ? "bg-amber-50/30" : ln.edited ? "bg-sky-50/40" : "hover:bg-zinc-50/60"}`} data-testid={`cl-row-${ln.id}`}>
                        <td className="px-3 py-1.5 align-middle">
                          <div className="flex flex-col gap-1">
                            {ln.grupo === "retencion"
                              ? <Badge variant="outline" className="border-amber-200 bg-amber-50 text-[9px] text-amber-700">IRPF</Badge>
                              : hasIssues ? <Badge variant="outline" className="border-rose-200 bg-rose-50 text-[9px] text-rose-700"><AlertTriangle className="mr-0.5 h-2.5 w-2.5" />Revisar</Badge>
                                : <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[9px] text-emerald-700">OK</Badge>}
                            {ln.edited && <Badge variant="outline" className="border-sky-200 bg-sky-50 text-[9px] text-sky-700"><Pencil className="mr-0.5 h-2.5 w-2.5" />Editada</Badge>}
                            {ln.file_id ? (
                              <button onClick={() => setPdfLine(ln)} title={ln.filename || "Ver factura"} data-testid={`cl-ref-${ln.id}`}
                                className="flex items-center gap-1 text-[9px] font-medium text-primary hover:underline">
                                <ReceiptText className="h-2.5 w-2.5 shrink-0" /><span className="max-w-[88px] truncate">{ln.filename || "Ver factura"}</span>
                              </button>
                            ) : (
                              <span className="text-[9px] text-muted-foreground">sin PDF</span>
                            )}
                          </div>
                        </td>
                        {cols.map((col) => {
                          const isEditing = editing && editing.id === ln.id && editing.col === col;
                          const val = ln.data?.[col] ?? "";
                          return (
                            <td key={col} className="px-2 py-1.5 align-middle">
                              {isEditing ? (
                                col === "Num. Cuenta de gasto" ? (
                                  <select autoFocus value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={commitEdit}
                                    onKeyDown={(e) => e.key === "Enter" ? commitEdit() : e.key === "Escape" ? cancelEdit() : null}
                                    className="w-full rounded border border-primary bg-white px-1 py-0.5 text-xs tabular-nums" data-testid={`cl-edit-${ln.id}-${col}`}>
                                    <option value="">— sin cuenta —</option>
                                    {cuentas.map((cu) => <option key={cu.numero} value={cu.numero}>{cu.label}</option>)}
                                  </select>
                                ) : (
                                  <input autoFocus value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={commitEdit}
                                    onKeyDown={(e) => e.key === "Enter" ? commitEdit() : e.key === "Escape" ? cancelEdit() : null}
                                    className="w-full min-w-[80px] rounded border border-primary bg-white px-1 py-0.5 text-xs" data-testid={`cl-edit-${ln.id}-${col}`} />
                                )
                              ) : (
                                <div onClick={() => startEdit(ln.id, col, val)} title={String(val)} data-testid={`cl-cell-${ln.id}-${col}`}
                                  className="min-h-[22px] max-w-[220px] cursor-text truncate rounded px-1.5 py-0.5 text-[13px] tabular-nums transition-colors hover:bg-primary/5">
                                  {val === "" || val === null || val === undefined ? <span className="text-zinc-300">—</span> : String(val)}
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-2 py-1.5 align-middle">
                          <div className="flex items-center justify-end gap-1">
                            {ln.file_id && (
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setPdfLine(ln)} title="Ver PDF" data-testid={`cl-pdf-${ln.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                            )}
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => { if (window.confirm("¿Eliminar esta línea del Excel del cierre?")) delMut.mutate(ln.id); }} title="Eliminar línea" data-testid={`cl-del-${ln.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-emerald-50/40 p-4">
            <div className="flex items-center gap-2 text-sm text-emerald-900">
              <Globe2 className="h-4 w-4" /> {c.paises ?? 0} país(es) · descarga el Excel ya corregido o regenera el ZIP del cierre.
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={downloadPais} onChange={(e) => setDownloadPais(e.target.value)} className="h-9 rounded-lg border bg-white px-2 text-sm" data-testid="cl-download-pais">
                <option value="Todos">Todos los países</option>
                {(data?.paises || []).map((p) => <option key={p} value={p}>{p}</option>)}
                {(c.retencion ?? 0) > 0 && <option value="Retención">Retención (IRPF)</option>}
              </select>
              <Button variant="outline" onClick={downloadExcel} data-testid="cl-download-excel"><Download className="mr-1.5 h-4 w-4" />Descargar Excel</Button>
              <Button onClick={() => reexportMut.mutate()} disabled={reexportMut.isPending} data-testid="cl-reexport">
                {reexportMut.isPending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Re-exportando…</> : <><FileSpreadsheet className="mr-1.5 h-4 w-4" />Re-exportar ZIP del cierre</>}
              </Button>
            </div>
          </div>
        </div>

        {pdfLine && <LinePdf cid={cid} line={pdfLine} onClose={() => setPdfLine(null)} />}
      </DialogContent>
    </Dialog>
  );
}

function LinePdf({ cid, line, onClose }) {
  const [pdf, setPdf] = useState(null);
  const [err, setErr] = useState(false);
  React.useEffect(() => {
    let cancelled = false;
    api.get(`/contabilidad/pdf/${line.file_id}`, { responseType: "arraybuffer" })
      .then((res) => { if (!cancelled) setPdf(new Uint8Array(res.data)); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [line.file_id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0 gap-0" data-testid="cl-pdf-dialog">
        <DialogHeader className="flex-row items-center justify-between border-b px-4 py-2.5">
          <DialogTitle className="text-sm">{line.filename || "Factura"}</DialogTitle>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}><X className="h-4 w-4" /></Button>
        </DialogHeader>
        <div className="h-[72vh] bg-muted/30">
          {err ? <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No se pudo cargar el PDF.</div> : <PdfViewer data={pdf} loading={!pdf} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
